import { useState, useEffect, useRef } from "react";
import { Button, Modal, Space, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { toastError, toastSuccess } from "helpers/toasters";
import { deleteLuckyHourItemApi, getLuckyHourItemsApi } from "Network";

// components
import GridTable from "modules/SubComponents/GridTable/GridTable";
import Header from "modules/SubComponents/Header";
import SubHeader from "modules/SubComponents/SubHeader";
import ItemModal from "modules/Items/ItemModal";

function LuckyHour() {
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("add");
  const [rowData, setRowData] = useState({});
  const [dataSource, setDataSource] = useState([]);
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [isDeleteItemOpen, setIsDeleteItemOpen] = useState(false);
  const [renderMe, setRenderMe] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  useEffect(() => {
    getItems();
  }, [renderMe]);

  const getItems = () => {
    getLuckyHourItemsApi(
      (success) => {
        if (success.success) {
          setLoading(false);
          setDataSource(success.data);
        } else {
          setLoading(false);
          toastError(success.message);
        }
      },
      (fail) => {
        if (fail.data?.error) {
          setLoading(false);
          toastError(fail.message);
        } else {
          setLoading(false);
          toastError(fail.message);
        }
      },
    );
  };

  const handleEditBtn = (e, row) => {
    setMode("edit");
    setRowData(row);
    setIsItemModalVisible(true);
  };

  const handlDeleteBtn = (e, row) => {
    setIsDeleteItemOpen(true);
    setRowData(row);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      (record[dataIndex] ?? "")
        .toString()
        .toLowerCase()
        .includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      key: "companyId",
      title: "COMPANY NAME",
      dataIndex: "companyName",
      ...getColumnSearchProps("companyName"),
      render: (_, row) => <span>{row.companyName ? row.companyName : ""}</span>,
    },
    {
      key: "description",
      title: "DESCRIPTION",
      dataIndex: "description",
      ...getColumnSearchProps("description"),
      render: (_, row) => (
        <span>{`${
          row?.description?.length > 50
            ? row.description.slice(0, 50) + "..."
            : row.description
        }`}</span>
      ),
    },
    {
      key: "isActive",
      title: "IS ACTIVE",
      dataIndex: "isActive",
      filters: [
        {
          text: "Active",
          value: true,
        },
        {
          text: "Deactive",
          value: false,
        },
      ],
      onFilter: (value, record) => value === record.isActive,
      render: (_, row) => <span>{row.isActive ? "Active" : "Deactive"}</span>,
    },
    {
      key: "price",
      title: "PRICE/EGP",
      dataIndex: "price",
      render: (_, row) => {
        let units = row.itemUnits;
        return <span>{`${units?.length ? units[0]?.price : ""} `}</span>;
      },
    },
    {
      key: "periodToExtendInDays",
      title: "PERIOD TO EXTEND/DAYS",
      render: (_, row) => (
        <p className="text-center">
          {row?.periodToExtendInDays}
          {row?.periodToExtendInDays === 0
            ? ""
            : row?.periodToExtendInDays === 1
              ? " day"
              : " days"}
        </p>
      ),
    },
    {
      key: "categoryName",
      title: "TYPE",
      dataIndex: "categoryName",
      ...getColumnSearchProps("categoryName"),
      render: (_, row) => {
        return (
          <span className="d-inline-block text-truncate text-center">
            {row.categoryName ? row.categoryName : "none"}
          </span>
        );
      },
    },
    {
      key: "quantityPerKilo",
      title: "QUANTITY/KILO",
      dataIndex: "quantityPerKilo",
      ...getColumnSearchProps("quantityPerKilo"),
    },
    {
      key: "totalUnits",
      title: "TOTAL UNITS",
      dataIndex: "totalUnits",
      ...getColumnSearchProps("totalUnits"),
      render: (_, row) => {
        return <span>{row.totalUnits.toFixed(3)}</span>;
      },
    },
    {
      key: "unitValue",
      title: "UNIT VALUE",
      dataIndex: "unitValue",
      ...getColumnSearchProps("unitValue"),
    },
    {
      key: "creationDate",
      title: "CREATION DATE",
      dataIndex: "creationDate",
      ...getColumnSearchProps("creationDate"),
    },
    {
      key: "id",
      title: "ACTIONS",
      dataIndex: "action",
      render: (_, row) => {
        return (
          <Space size={5}>
            <Button
              onClick={(e) => handleEditBtn(e, row)}
              style={{ backgroundColor: "green", color: "white" }}
            >
              Edit
            </Button>
            <Button type="danger" onClick={(e) => handlDeleteBtn(e, row)}>
              Delete
            </Button>
          </Space>
        );
      },
    },
  ];

  const handleAddItems = () => {
    setIsItemModalVisible(true);
    setMode("add");
  };

  const onCancel = () => {
    setIsItemModalVisible(false);
    setRenderMe((prev) => !prev);
    setMode("add");
  };

  const handleCancel = () => {
    setIsDeleteItemOpen(false);
    // setIsOfferModalOpen(false);
  };

  const handleOk = () => {
    const data = { itemId: rowData?.itemId };
    deleteLuckyHourItemApi(
      data,
      (success) => {
        if (success?.success) {
          toastSuccess(success.message);
          setRenderMe((prev) => !prev);
          handleCancel();
        } else {
          toastError(success.message);
          handleCancel();
        }
      },
      (fail) => {
        toastError(fail.message);
      },
    );
  };

  return (
    <section className="users">
      <div className="users__all-contents ">
        <div className="users__content">
          <div className="users__usersData">
            <Header
              title="Discounted Prices"
              plusButtonName={"Add Item"}
              plusButtonAction={handleAddItems}
            />
            <SubHeader bgColor="#356ab3" title="All Discounted Prices Items" />
            <div className="users__table grid-table mrg-ye-50 ">
              <GridTable
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                rowId={"itemId"}
              />
            </div>
          </div>
        </div>
        {isItemModalVisible && (
          <ItemModal
            isItemModalVisible={isItemModalVisible}
            mode={mode}
            rowData={rowData}
            page={"lucky-hour"}
            SubHeader={
              <SubHeader
                bgColor="#F58234"
                title={`${mode === "add" ? "Add New Item" : "Edit Item"}`}
                closeButton
                onCancel={onCancel}
              />
            }
            onCancel={onCancel}
            primaryBtnName={`${mode === "add" ? "Add" : "Edit"}`}
            secondaryBtnName={"Cancel"}
          />
        )}
        {isDeleteItemOpen && (
          <Modal
            title="Delete Item"
            open={isDeleteItemOpen}
            onOk={handleOk}
            onCancel={handleCancel}
          >
            <span>{"Are you sure you want to delete this item?"}</span>
          </Modal>
        )}
      </div>
    </section>
  );
}

export default LuckyHour;
